import React, {
  useState, useEffect, useMemo, forwardRef,
} from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/components/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './MyPlan.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, IconRefresh, Skeleton, Tag,
} from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import RebootModal from './RebootModal';
import RebootHistoryModal from './RebootHistoryModal';
import { vpsOciActions } from '@/redux/modules';
import { ENABLE_VPS_OCI_DETAIL_PAGE_STATUS } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useParams } from 'react-router-dom';

const MyPlan = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [showRebootHistoryModal, setShowRebootHistoryModal] = useState(false);
  const isVpsOCIStatusEnabled = useFeatureIsOn(ENABLE_VPS_OCI_DETAIL_PAGE_STATUS);

  const vpsOci = useSelector(state => state.vpsOci);
  const { vps } = (vpsOci && vpsOci.data) || {};

  const isRebooting = useSelector(state => state.vpsOci?.isRebooting);
  const statusData = useSelector(state => state.vpsOci?.status?.value);
  const loadingStatus = useSelector(state => state.vpsOci?.status?.loading);
  const loadingRebuild = useSelector(state => state.vpsOci?.rebuild?.loading);
  const loadingChangeImage = useSelector(state => state.vpsOci?.changeImage?.loading);
  const isRebuilding = loadingRebuild || loadingChangeImage;

  const isVpsOciOnline = useSelector(state => state.vpsOci?.status?.value === 'online');
  const isDisabledButton = isRebooting || isRebuilding || !isVpsOciOnline;

  const { id: hostingId } = useParams();

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const status = useMemo(() => {
    if (isRebooting || isRebuilding) {
      return {
        value: locale.status.offline,
        variant: 'neutral',
      };
    }

    switch (statusData) {
      case 'online':
        return {
          value: locale.status.online,
          variant: 'success',
        };
      case 'offline':
        return {
          value: locale.status.offline,
          variant: 'neutral',
        };
      case 'processing':
        return {
          value: locale.status.processing,
          variant: 'warning',
        };
      case 'rebuild':
        return {
          value: locale.status.rebuild,
          variant: 'warning',
        };
      default:
        return null;
    }
  }, [isRebooting, isRebuilding, statusData, locale]);

  useEffect(() => {
    if (hostingId && !statusData) {
      dispatch(vpsOciActions.status.get({ hostingId }));
    }
  }, [dispatch, hostingId, statusData]);

  if (!vpsOci || !vps) return null;

  const toggleModals = (modalToShow) => {
    setShowRebootModal(modalToShow === 'reboot');
    setShowRebootHistoryModal(modalToShow === 'rebootHistory');
  };

  return (
    <>
      <ContentSection ref={ref} direction="row" align="center" justify="space-between" testId="vpsoci-myplan-section">
        <Styles.InfoWrapper>
          <Styles.Title data-testid="vpsoci-myplan-title">{locale.myPlan.title}</Styles.Title>
          <Styles.PlanInfo>
            <Styles.ProductName data-testid="vpsoci-myplan-product-name">{vpsOci.data.productName}</Styles.ProductName>
            {(() => {
              if (!isVpsOCIStatusEnabled) return null;
              if (loadingStatus && !isRebooting && !isRebuilding) return <Skeleton width="100px" height="28px" />;
              if (status) return <Tag label={status.value} variant={status.variant} size="md" testId={`vpsoci-myplan-status-tag-${statusData}`} />;
              return null;
            })()}
          </Styles.PlanInfo>
        </Styles.InfoWrapper>
        <Styles.ButtonWrapper>
          <Button
            label={locale.myPlan.reboot}
            variant="secondary"
            size="medium2"
            iconLeft={!isRebooting && <IconRefresh color={isDisabledButton ? 'lowLight' : 'primaryMedium'} />}
            notFillSVG
            width={isMobile ? 'full' : 'fit-content'}
            onClick={!isDisabledButton ? () => setShowRebootModal(true) : undefined}
            disabled={isDisabledButton}
            loading={isRebooting}
            testId="my-plan-reboot-button"
          />
        </Styles.ButtonWrapper>
      </ContentSection>
      {showRebootModal && (
        <RebootModal
          onClose={() => setShowRebootModal(false)}
          onConfirm={() => dispatch(vpsOciActions.reboot.request(hostingId))}
          onCheckRebootHistory={() => toggleModals('rebootHistory')}
        />
      )}
      {showRebootHistoryModal && (
        <RebootHistoryModal
          onClose={() => setShowRebootHistoryModal(false)}
          onGoBack={() => toggleModals('reboot')}
        />
      )}
    </>
  );
});

MyPlan.displayName = 'MyPlan';

export default MyPlan;
